import { getWithAuth, getWithOutAuth, patchWithAuth, postWithAuth, postWithAuthServerSide, postWithOutAuth } from '@services/http.service';
import { UrlParamsReplace } from '@services/url.service';

import type { zoomAuthEventType } from '@lib/zoom/types';

const ZOOM_USER_PLATFORM_API = (id: string) => UrlParamsReplace('users/:id/zoom-platforms', { id });
const ZOOM_EVENTS = (queryParams: object) => UrlParamsReplace('platforms/zoom/events', {}, queryParams);
const ZOOM_EVENT_SESSIONS = (platformId: string, eventId: string) => UrlParamsReplace('platforms/zoom/events/:eventId/sessions', { eventId }, { platformId });
const ZOOM_EVENT_JOIN_TOKEN = (eventId: string, sessionId: string) => UrlParamsReplace('platforms/zoom/events/:eventId/sessions/:sessionId/join-token', { eventId, sessionId });
const ZOOM_GET_CHALLENGE_CODE = (queryParams: object) => UrlParamsReplace('platforms/zoom/get-challenge-code', {}, queryParams);
const ZOOM_AUTH_CALLBACK = (eventPayload: zoomAuthEventType) => UrlParamsReplace('platforms/zoom/auth', {}, eventPayload);
const ZOOM_RESOURCE_API = (resourceType: string, resourceId: string, queryParams: object) => UrlParamsReplace(
  'platforms/zoom/:resourceType/:resourceId',
  { resourceType, resourceId },
  queryParams
);
const ZOOM_REGISTER_RESOURCE_API = (resourceType: string, resourceId: string) => UrlParamsReplace(
  'platforms/zoom/:resourceType/:resourceId/register',
  { resourceType, resourceId }
);
const ZOOM_GET_RECORDING_TOKEN_API = (resourceType: string, resourceId: string) => UrlParamsReplace(
  'platforms/zoom/:resourceType/:resourceId/local-recording-token',
  { resourceType, resourceId }
);
const ZOOM_GET_ZAK_TOKEN_API = (queryParams: object) => UrlParamsReplace(
  '/platforms/zoom/zak',
  {},
  queryParams
);

const ZOOM_GET_OBF_TOKEN_API = () => UrlParamsReplace('/platforms/zoom/obf');

const ZOOM_RESOURCES_API = (resourceType: string, queryParams: object) => UrlParamsReplace(
  'platforms/zoom/:resourceType',
  { resourceType },
  queryParams
);

const ZOOM_DEEPLINK_API = () => UrlParamsReplace(
  'platforms/zoom/deeplink',
  {}
);

const ZOOM_DECRYPT_CONTEXT_API = () => UrlParamsReplace(
  'platforms/zoom/context/decrypt',
  {}
);

const ZOOM_GET_PLATFORM_API = (platformId: string) => UrlParamsReplace(
  'platforms/zoom/platforms/:platformId',
  { platformId }
);

const ZOOM_COMPLETE_ONBOARDING_API = (platformId: string) => UrlParamsReplace(
  'platforms/zoom-platform/:platformId/onboarding-complete',
  { platformId }
);

const ZOOM_USER_MEETING_UPDATE_API = (id: string) => UrlParamsReplace(
  'platforms/zoom/:id',
  { id }
);

export const getZoomUserPlatform = (id: string) => getWithAuth(ZOOM_USER_PLATFORM_API(id));

export const getZoomEvents = (queryParams: object) => getWithAuth(ZOOM_EVENTS(queryParams), true);

export const getZoomEventSessions = (platformId: string, eventId: string) => getWithAuth(ZOOM_EVENT_SESSIONS(platformId, eventId), true);

export const getZoomEventJoinToken = (eventId: string, sessionId: string) => getWithAuth(ZOOM_EVENT_JOIN_TOKEN(eventId, sessionId), true);

export const getZoomChallengeCode = (queryParams: object) => getWithAuth(ZOOM_GET_CHALLENGE_CODE(queryParams));

export const zoomAuthCallback = (eventPayload: zoomAuthEventType) => getWithOutAuth(ZOOM_AUTH_CALLBACK({ ...eventPayload, state: decodeURIComponent(eventPayload.state) }));

export const getZoomResource = (resourceType: string, resourceId: string, queryParams: object, rejectFalseResponse = true) => getWithAuth(ZOOM_RESOURCE_API(resourceType, resourceId, queryParams), rejectFalseResponse);

export const registerForResource = (resourceType: string, resourceId: string, payload: object) => postWithAuth(ZOOM_REGISTER_RESOURCE_API(resourceType, resourceId), payload);

export const getZoomRecordingToken = (resourceType: string, resourceId: string, payload: object) => postWithAuth(ZOOM_GET_RECORDING_TOKEN_API(resourceType, resourceId), payload);

export const getZoomZakToken = (queryParams: object) => getWithAuth(ZOOM_GET_ZAK_TOKEN_API(queryParams));

export const getZoomOBFToken = (payload: object) => postWithOutAuth(ZOOM_GET_OBF_TOKEN_API(), payload);

export const getZoomResources = (resourceType: string, queryParams: object, rejectFalseResponse = true) => getWithAuth(ZOOM_RESOURCES_API(resourceType, queryParams), rejectFalseResponse);

export const getDeeplink = (payload: object) => postWithAuth(ZOOM_DEEPLINK_API(), payload);

export const decryptZoomAppContext = (context: string) => postWithOutAuth(ZOOM_DECRYPT_CONTEXT_API(), { context });

export const getDeepLinkServerSide = (auth: object, payload: object) => postWithAuthServerSide(ZOOM_DEEPLINK_API(), auth, null, payload);

export const getZoomPlatform = (platformId: string) => getWithOutAuth(ZOOM_GET_PLATFORM_API(platformId));

export const completeZoomOnboarding = ({ platformId, totalResponders }: { platformId: string, totalResponders: number }) => patchWithAuth(ZOOM_COMPLETE_ONBOARDING_API(platformId), { totalResponders });

export const updateZoomMeeting = (id: string, data: object) => patchWithAuth(ZOOM_USER_MEETING_UPDATE_API(id), data);
